// Left zone (prototype renderQueue / qcard / pcard): Queue / Parked / Sent to me.
// Queue cards: severity stripe, label, age (amber past slaDueAt), site, analytic and camera, alarm and camera
// counts, snapshot, commissioning pill, "follows E-…", and who it is offered to or waiting for.
//   pull mode: clicking a card claims it (cap from /config.json).
//   push mode: cards are read-only for operators (the router offers events); a Claim button shows only when
//              routing.allowPull is on. Supervisors and admins open any card read-only and keep Take.
// Parked: my parked events with reason, parked-for timer, fallback countdown and Resume; Phase 4: the AI watch line
// ("AI watching Cam 01, 02 for police · 06:12", confirming, or the hit with its frame).
// Sent to me: events transferred or raised to me or to one of my groups, with the transfer note.

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { parkReasonLabel, type EventSummary, type FeaturesConfig, type Watch } from '../api.ts';
import type { RoutingConfig } from '../auth.ts';
import { AuthImg } from '../components/AuthMedia.tsx';
import { Icon } from '../components/Icon.tsx';
import { ErrorBox, Loading } from '../components/ui.tsx';
import { useNow } from '../hooks.ts';
import { QueueAiLine } from './Agent.tsx';
import { Since } from './Ticks.tsx';
import { WatchLine } from './Watches.tsx';
import { camShort, dur, human, ms, plural, SEV_CLS, SEV_LABEL } from './util.ts';

export type QTab = 'queue' | 'parked' | 'shared';

export interface QueueProps {
  items: EventSummary[] | undefined;
  error: unknown;
  reload: () => void;
  features: FeaturesConfig;
  routing: RoutingConfig;
  activeCount: number;
  maxActive: number;
  /** Operator role, under the cap, and claiming is allowed by the routing mode. */
  canClaim: boolean;
  /** Operator role (Claim buttons show, disabled at the cap). */
  isOperator: boolean;
  /** Supervisor / admin: opening a card shows it read-only; Take. */
  isSup: boolean;
  openId: string | null;
  /** Claim or Take in flight for this id. */
  busyId: string | null;
  /** My parked events. */
  parked: EventSummary[];
  /** Phase 4: AI watches per parked event (features.agent). */
  watchesOf?: (eventId: string) => Watch[] | undefined;
  /** Transferred / raised to me or my groups. */
  sentToMe: EventSummary[];
  meId: string;
  onOpen: (e: EventSummary) => void;
  onClaim: (e: EventSummary) => void;
  onTake: (e: EventSummary) => void;
  onResume: (e: EventSummary) => void;
  tab: QTab;
  setTab: (t: QTab) => void;
}

/** "offered to J. Ortiz · 0:09" / "waiting for Tier 1 · Night". */
export function waitingFor(e: EventSummary, now: number, meId: string): string | null {
  if (e.offer) {
    const left = Math.max(0, (ms(e.offer.expiresAt) ?? now) - now);
    return `offered to ${e.offer.userId === meId ? 'you' : e.offer.userName} · ${dur(left).replace(/^00:/, '0:')}`;
  }
  const t = e.target;
  if (!t) return null;
  if (t.userId) return `for ${t.userId === meId ? 'you' : (t.userName ?? 'an operator')}`;
  if (t.groupName) return `for ${t.groupName}`;
  return null;
}

function QCard({
  e,
  isNew,
  open,
  busy,
  clickable,
  onOpen,
  hint,
  meId,
  children,
}: {
  e: EventSummary;
  isNew: boolean;
  open: boolean;
  busy: boolean;
  clickable: boolean;
  onOpen: () => void;
  hint: string;
  meId: string;
  children?: ReactNode;
}) {
  const now = useNow(1000);
  const onKey = (k: KeyboardEvent<HTMLDivElement>) => {
    if (k.target !== k.currentTarget) return;
    if (k.key === 'Enter' || k.key === ' ') {
      k.preventDefault();
      if (clickable) onOpen();
    }
  };
  const wait = waitingFor(e, now, meId);
  return (
    <div
      className={`qcard s-${SEV_CLS[e.severity]}${isNew ? ' new' : ''}${open ? ' open' : ''}${clickable ? '' : ' ro'}`}
      role={clickable ? 'button' : 'article'}
      tabIndex={0}
      aria-label={`${SEV_LABEL[e.severity]} ${e.siteName}, ${human(e.primaryAnalytic)}. ${hint}`}
      title={hint}
      onClick={(x) => {
        if ((x.target as HTMLElement).closest('button')) return;
        if (clickable) onOpen();
      }}
      onKeyDown={onKey}
    >
      <div className="qthumb">
        {e.snapshotUrl ? <AuthImg url={e.snapshotUrl} alt="" lazy fallback="" /> : <span className="qph"><Icon name="cam" /></span>}
        {busy && (
          <span className="qbusy">
            <span className="spinner" aria-hidden="true" />
          </span>
        )}
      </div>
      <div className="qtxt">
        <div className="qrow">
          <span className="sev">{SEV_LABEL[e.severity]}</span>
          <Since from={e.firstAlarmAt} lateAt={e.slaDueAt} className="age" />
        </div>
        <div className="qsite">{e.siteName}</div>
        <div className="qmeta">
          {human(e.primaryAnalytic)} · {camShort(e.primaryCameraName)}
        </div>
        <div className="qalarms">
          {plural(e.alarmCount, 'alarm')} · {plural(e.cameraCount, 'cam')}
          {e.storm && <span className="pill warn tiny-pill">storm</span>}
          {e.overflowed && (
            <span className="pill info tiny-pill" title="Nobody in the first group was free in time, so it moved to the overflow group">
              overflow
            </span>
          )}
        </div>
        <QueueAiLine e={e} />
        {e.commissioning && (
          <div className="qnote">
            <span className="pill warn tiny-pill" title="Site not live yet: only supervisors and admins see this">
              commissioning
            </span>
          </div>
        )}
        {e.follows && (
          <div className="qnote">
            <Icon name="link" />
            follows <span className="mono">{e.follows.displayNo}</span>
          </div>
        )}
        {wait && (
          <div className={`qnote${e.offer ? ' offered' : ''}`}>
            <Icon name={e.offer ? 'inbox' : 'route'} />
            {wait}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

function PCard({ e, onResume, onOpen, canResume, busy, open, watches }: { e: EventSummary; onResume: () => void; onOpen: () => void; canResume: boolean; busy: boolean; open: boolean; watches?: Watch[] }) {
  const now = useNow(1000);
  const p = e.park;
  const fb = ms(p?.fallbackAt);
  const left = fb === null ? null : fb - now;
  return (
    <div className={`pcard s-${SEV_CLS[e.severity]}${e.raised ? ' hit' : ''}${open ? ' open' : ''}`}>
      <div className="qrow">
        <span className="sev">{parkReasonLabel(p?.reason).toUpperCase()}</span>
        <span className="age">
          parked <Since from={p?.parkedAt ?? null} />
        </span>
      </div>
      <button type="button" className="qsite linkish" onClick={onOpen} title="Open read-only">
        {e.siteName}
      </button>
      <div className="qmeta">
        {human(e.primaryAnalytic)} · {camShort(e.primaryCameraName)} · {plural(e.alarmCount, 'alarm')}
      </div>
      <QueueAiLine e={e} />
      {p?.note && <div className="qnote pnote">{p.note}</div>}
      <WatchLine ws={watches} />
      <div className="qrow">
        <span className={`note${left !== null && left < 60_000 ? ' warn-t' : ''}`}>
          {left === null ? 'No fallback timer' : left > 0 ? <>Raises in <span className="mono">{dur(left)}</span></> : 'Raising…'}
        </span>
        <button type="button" className="btn sm" onClick={onResume} disabled={!canResume || busy} title={canResume ? 'Take it back as an active event' : 'At your active cap: park or close one first'}>
          {busy ? 'Resuming…' : 'Resume'}
        </button>
      </div>
    </div>
  );
}

export function Queue(p: QueueProps) {
  const { tab, setTab } = p;
  const seen = useRef<Set<string> | null>(null);
  // The server sorts: priority desc, slaDueAt asc, queuedAt asc.
  const items = p.items ?? [];
  const push = p.routing.mode === 'push';
  // Cards that arrive after the first load slide in.
  const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (!p.items) return;
    const ids = p.items.map((e) => e.id);
    if (seen.current) {
      const prev = seen.current;
      const n = ids.filter((id) => !prev.has(id));
      if (n.length) setFresh(new Set(n));
    }
    seen.current = new Set(ids);
  }, [p.items]);

  const atCap = p.activeCount >= p.maxActive;
  const hot = items.some((e) => e.severity === 'critical');
  const counts = { crit: 0, high: 0, med: 0, low: 0 };
  for (const e of items) {
    if (e.severity === 'critical') counts.crit++;
    else if (e.severity === 'high') counts.high++;
    else if (e.severity === 'medium') counts.med++;
    else counts.low++;
  }
  const total = Math.max(1, items.length);

  // Pull mode (or push with allowPull): an operator under the cap claims. Push without allowPull: read-only.
  const claimable = p.isOperator && (!push || p.routing.allowPull);
  const cardClick = (e: EventSummary): boolean => (!push && p.canClaim) || p.isSup || p.openId === e.id;

  function hintFor(e: EventSummary): string {
    if (p.openId === e.id) return 'Open in the workspace';
    if (!push && p.canClaim) return 'Claim and open';
    if (p.isSup) return 'Open read-only (Take from the card or the event header)';
    if (push && !p.routing.allowPull) return 'Push routing: events are offered to you';
    if (atCap) return `You have ${p.activeCount}/${p.maxActive} active. Park or close one to claim another.`;
    return claimable ? 'Claim it with the Claim button' : 'You cannot claim events';
  }

  function cardFor(e: EventSummary, extra?: ReactNode) {
    const showClaim = push && claimable;
    const showTake = p.isSup && (push || !p.canClaim);
    return (
      <QCard
        key={e.id}
        e={e}
        isNew={fresh.has(e.id)}
        open={p.openId === e.id}
        busy={p.busyId === e.id}
        clickable={cardClick(e)}
        hint={hintFor(e)}
        meId={p.meId}
        onOpen={() => p.onOpen(e)}
      >
        {extra}
        {(showClaim || showTake) && (
          <div className="qbtns">
            {showClaim && (
              <button type="button" className="btn sm pri" onClick={() => p.onClaim(e)}
                disabled={!p.canClaim || p.busyId === e.id || !!e.offer}
                title={e.offer ? `Offered to ${e.offer.userId === p.meId ? 'you' : e.offer.userName} right now` : p.canClaim ? 'Claim it now' : 'At your active cap'}
              >
                Claim
              </button>
            )}
            {showTake && (
              <button type="button" className="btn sm" onClick={() => p.onTake(e)} disabled={p.busyId === e.id} title="Supervisor: take it as your active event">
                <Icon name="users" />
                Take
              </button>
            )}
          </div>
        )}
      </QCard>
    );
  }

  let list: ReactNode;
  if (tab === 'parked') {
    list =
      p.parked.length === 0 ? (
        <div className="note qempty">No parked events. Park (P) frees a slot while you wait on police or a callback.</div>
      ) : (
        p.parked.map((e) => (
          <PCard key={e.id} e={e} open={p.openId === e.id} busy={p.busyId === e.id} canResume={!atCap} watches={p.watchesOf?.(e.id)} onResume={() => p.onResume(e)} onOpen={() => p.onOpen(e)} />
        ))
      );
  } else if (tab === 'shared') {
    list =
      p.sentToMe.length === 0 ? (
        <div className="note qempty">Nothing sent to you or your groups.</div>
      ) : (
        p.sentToMe.map((e) =>
          cardFor(
            e,
            e.transferNote ? (
              <div className="xnote">
                <Icon name="note" />
                <span>{e.transferNote}</span>
              </div>
            ) : e.raised ? (
              <div className="qnote">
                <Icon name="park" />
                raised from park
              </div>
            ) : null,
          ),
        )
      );
  } else if (p.error && !p.items) {
    list = <ErrorBox error={p.error} onRetry={p.reload} title="Could not load the queue" />;
  } else if (!p.items) {
    list = <Loading label="Loading queue…" />;
  } else if (items.length === 0) {
    list = <div className="note qempty">{push ? 'Queue is clear. New events will be offered to you.' : 'Queue is clear. New events appear here as alarms arrive.'}</div>;
  } else {
    list = items.map((e) => cardFor(e));
  }

  const parkedHot = p.parked.some((e) => e.raised);
  return (
    <aside className="pane zq" aria-label="Event queue">
      <div className="qhead">
        <div className="seg full" role="tablist" aria-label="Queue views">
          <button type="button" role="tab" aria-selected={tab === 'queue'} className={tab === 'queue' ? 'on' : ''} onClick={() => setTab('queue')}>
            Queue <span className={`n ${hot ? 'hot' : ''}`}>{items.length}</span>
          </button>
          {p.features.park && (
            <button type="button" role="tab" aria-selected={tab === 'parked'} className={tab === 'parked' ? 'on' : ''} onClick={() => setTab('parked')}>
              Parked <span className={`n ${parkedHot ? 'hot' : ''}`}>{p.parked.length}</span>
            </button>
          )}
          {p.features.transfer && (
            <button type="button" role="tab" aria-selected={tab === 'shared'} className={tab === 'shared' ? 'on' : ''} onClick={() => setTab('shared')}>
              Sent to me <span className={`n ${p.sentToMe.length ? 'hot' : ''}`}>{p.sentToMe.length}</span>
            </button>
          )}
        </div>
        <div className="qroute">
          <span>
            Routing: <b>{push ? 'Push' : 'Pull'}</b> · {push ? (p.routing.allowPull ? 'offers, or claim' : 'next offer auto') : 'you pick'}
          </span>
          <span className={atCap ? 'capwarn' : ''} title={atCap ? 'At your active-event cap' : undefined}>
            {p.activeCount}/{p.maxActive} active
          </span>
        </div>
        {atCap && p.isOperator && p.activeCount > 0 && (
          <div className="capnote" role="note">
            <Icon name="lock" />
            At your cap. {p.features.park ? 'Park or close' : 'Close'} an event to take the next one.
          </div>
        )}
      </div>
      <div className="qlist">
        {list}
        {tab === 'queue' && p.error && p.items ? <div className="note warn-t">Queue refresh failed; showing the last list.</div> : null}
      </div>
      <div className="qfoot">
        <div className="lbl">Queue by severity</div>
        <div className="bar" aria-hidden="true">
          <i style={{ width: `${(counts.crit / total) * 100}%`, background: 'var(--crit)' }} />
          <i style={{ width: `${(counts.high / total) * 100}%`, background: 'var(--high)' }} />
          <i style={{ width: `${(counts.med / total) * 100}%`, background: 'var(--med)' }} />
          <i style={{ width: `${(counts.low / total) * 100}%`, background: 'var(--low)' }} />
        </div>
        <div className="legend">
          <span style={{ color: 'var(--crit)' }}>{counts.crit} crit</span>
          <span style={{ color: 'var(--high)' }}>{counts.high} high</span>
          <span style={{ color: 'var(--med)' }}>{counts.med} med</span>
          <span style={{ color: 'var(--low)' }}>{counts.low} low</span>
        </div>
      </div>
    </aside>
  );
}
